import { motion } from 'framer-motion';
import { MessageSquare, Users, Brain, Trophy, Sparkles, Zap } from 'lucide-react';
import { Card } from '@/components/ui/Card';

const features = [
  {
    icon: MessageSquare,
    title: 'Strategic Communication',
    description: 'Present ideas that inspire action and drive decisions',
    gradient: 'from-primary/10 to-violet-500/10',
    iconBg: 'bg-primary/15 text-primary',
  },
  {
    icon: Users,
    title: 'Strategic Networking',
    description: 'Build relationships that accelerate opportunities',
    gradient: 'from-emerald-500/10 to-teal-500/10',
    iconBg: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  },
  {
    icon: Brain,
    title: 'AI Mastery',
    description: 'Leverage technology to stay ahead of industry changes',
    gradient: 'from-violet-500/10 to-purple-500/10',
    iconBg: 'bg-violet-500/15 text-violet-600 dark:text-violet-400',
  },
  {
    icon: Trophy,
    title: 'Executive Influence',
    description: 'Shape outcomes and guide critical business decisions',
    gradient: 'from-amber-500/10 to-orange-500/10',
    iconBg: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  },
  {
    icon: Sparkles,
    title: 'Executive Presence',
    description: 'Command respect and showcase your unique value',
    gradient: 'from-rose-500/10 to-pink-500/10',
    iconBg: 'bg-rose-500/15 text-rose-600 dark:text-rose-400',
  },
  {
    icon: Zap,
    title: 'Daily 5-Minute Actions',
    description: 'Personalized micro-actions that compound into massive growth',
    gradient: 'from-blue-500/10 to-cyan-500/10',
    iconBg: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  },
];

export const Features = () => {
  return (
    <section className="relative py-24 overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-primary/[0.02] to-transparent" />
      <div className="container relative mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight">
            <span className="text-foreground">Five Skills That </span>
            <span className="bg-gradient-to-r from-primary via-violet-500 to-fuchsia-500 bg-clip-text text-transparent">
              Transform Careers
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            Master these competencies to unlock exponential career growth
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.08 }}
            >
              <Card
                hover
                className={`p-6 sm:p-8 h-full rounded-2xl border-2 bg-gradient-to-br ${feature.gradient} hover:border-primary/40 transition-all duration-300 group overflow-hidden`}
              >
                <div className="flex flex-col">
                  <div className={`w-11 h-11 rounded-xl ${feature.iconBg} flex items-center justify-center mb-5 shrink-0`}>
                    <feature.icon className="w-5 h-5" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-3 group-hover:text-primary transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
